import emailjs from '@emailjs/browser';
import { openWhatsApp } from './whatsapp';

// EmailJS configuration
export const EMAILJS_CONFIG = {
  // Service ID from EmailJS
  SERVICE_ID: import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
  
  // Template IDs from EmailJS
  TEMPLATES: {
    // For sending to business email
    TO_BUSINESS: import.meta.env.VITE_EMAILJS_TEMPLATE_ID_BUSINESS || '',
    // For auto-reply to client
    TO_CLIENT: import.meta.env.VITE_EMAILJS_TEMPLATE_ID_CLIENT || ''
  },
  
  // Public key from EmailJS
  PUBLIC_KEY: import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '',
  
  // Business email for receiving inquiries
  BUSINESS_EMAIL: import.meta.env.VITE_EMAILJS_BUSINESS_EMAIL || ''
};

// Log configuration status
console.log('EmailJS Configuration Status:', {
  serviceId: !!EMAILJS_CONFIG.SERVICE_ID,
  templateToBusiness: !!EMAILJS_CONFIG.TEMPLATES.TO_BUSINESS,
  templateToClient: !!EMAILJS_CONFIG.TEMPLATES.TO_CLIENT,
  publicKey: !!EMAILJS_CONFIG.PUBLIC_KEY,
  businessEmail: !!EMAILJS_CONFIG.BUSINESS_EMAIL
});

export interface FormData {
  from_name: string;
  from_email: string;
  to_email: string;
  phone: string;
  loan_type: string;
  message: string;
  [key: string]: unknown;
}

/**
 * Sends form data to the business email and an auto-reply to the client
 * @param formData The form data containing client information
 * @returns Promise with success status and message
 */
/**
 * Formats form data into a WhatsApp message
 */
const formatWhatsAppMessage = (formData: FormData): string => {
  return `*New Form Submission*\n\n` +
         `*Name:* ${formData.from_name || 'Not provided'}\n` +
         `*Email:* ${formData.from_email || 'Not provided'}\n` +
         `*Phone:* ${formData.phone || 'Not provided'}\n` +
         `*Loan Type:* ${formData.loan_type || 'Not specified'}\n` +
         `*Message:* ${formData.message || 'No message provided'}\n\n` +
         `_Submitted on ${new Date().toLocaleString()}_`;
};

export const sendEmail = async (formData: FormData): Promise<{ success: boolean; message: string }> => {
  const { SERVICE_ID, TEMPLATES, PUBLIC_KEY, BUSINESS_EMAIL } = EMAILJS_CONFIG;
  
  try {
    console.log('Starting form submission process with data:', formData);

    // 1. Send WhatsApp notification
    const whatsappMessage = formatWhatsAppMessage(formData);
    openWhatsApp(whatsappMessage);
    console.log('📱 WhatsApp notification prepared');

    // 2. Send email to business
    const businessTemplateParams = {
      from_name: formData.from_name,
      from_email: formData.from_email,
      to_email: BUSINESS_EMAIL,
      phone: formData.phone,
      loan_type: formData.loan_type,
      message: formData.message,
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      company_name: 'White Pearl Finance',
      subject: `New Contact Form Submission from ${formData.from_name}`,
      to_name: 'Support Team',
      whatsapp_message: whatsappMessage.replace(/\n/g, '<br>') // For email template
    };

    // 3. First send email to business
    await emailjs.send(
      SERVICE_ID,
      TEMPLATES.TO_BUSINESS,
      businessTemplateParams,
      PUBLIC_KEY
    ).catch(e => {
      console.error('Failed to send business email:', e);
      throw e; // Re-throw to be caught by the outer catch
    });

    // 4. Then send auto-reply to client if email is provided
    if (formData.from_email) {
      const autoReplyParams = {
        to_email: formData.from_email,
        to_name: formData.from_name || 'Customer',
        from_name: 'White Pearl Finance',
        from_email: BUSINESS_EMAIL,
        reply_to: BUSINESS_EMAIL,
        subject: 'Thank you for your inquiry',
        message: `Dear ${formData.from_name || 'Valued Customer'},\n\nThank you for contacting White Pearl Finance. We have received your inquiry and our team will review your request.\n\nWe will get back to you within 24 hours.\n\nBest regards,\nWhite Pearl Finance Team`,
        phone: formData.phone || 'Not provided',
        loan_type: formData.loan_type || 'General Inquiry',
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        company_name: 'White Pearl Finance',
        whatsapp_number: '+91 93244 71736'
      };

      try {
        await emailjs.send(
          SERVICE_ID,
          TEMPLATES.TO_CLIENT,
          autoReplyParams,
          PUBLIC_KEY
        );
      } catch (e) {
        console.error('Failed to send client email:', e);
        // Don't re-throw, as we still want to proceed even if auto-reply fails
      }
    }

    console.log('✅ Form submission processed:', {
      whatsapp: 'prepared',
      businessEmail: 'sent',
      clientEmail: formData.from_email ? 'sent' : 'not sent',
      timestamp: new Date().toISOString()
    });

    return { 
      success: true, 
      message: 'Your request has been submitted. Please check your email for a confirmation and look for a WhatsApp message from our team.'
    };
    
  } catch (error) {
    console.error('❌ Form submission partially failed:', error);
    
    // Even if email fails, WhatsApp will still work
    return { 
      success: true, // Still return success since WhatsApp notification was prepared
      message: 'We\'ve prepared a WhatsApp message for you to complete your submission. Please check your WhatsApp messages to finalize your request.'
    };
  }
};

/**
 * Formats form data into a readable string
 */
export const formatContactMessage = (formData: Record<string, string>): string => {
  return Object.entries(formData)
    .filter(([_, value]) => value) // Filter out empty values
    .map(([key, value]) => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`)
    .join('\n');
};

